/**
 * API Session object.
 * Takes care of session token handling and
 * making it accessible to all API clients
 * within the application.
 */
export const APISession = {
  /**
   * Session token key for storing in the browser local storage
   */
  tokenKey: '401750d7-c33b-4117-906e-ffc8f7a9ea24',

  /**
   * Session token
   * @type {String}
   */
  token: null,

  /**
   * Current account
   * @type {Account}
   */
  account: null,

  /**
   * Handler to be called when session is being asked for,
   * but we're not logged in yet.
   * Useful in non-interactive processes as a place to implement re-login.
   * @type {Function}
   */
  loginHandler: null,

  /**
   * Error handler to be called when API calls
   * fail with authorization errors
   * @type {Function<Error>}
   */
  errorHandler: null,

  /**
   * Checks whether the session has been initialized.
   * This doesn't means though that the session token hasn't expired in the meantime!
   * This can only be determined by the server.
   * @returns {Boolean}
   */
  get hasSession () {
    return Boolean(this.token && this.account)
  },

  /**
   * Stores the current session token
   * @param {String} value Session token to store
   */
  setToken (value) {
    if (value) {
      return this.token = value
    }
  },

  /**
   * Stores the current session account
   * @param {Account} value Session account to store
   */
  setAccount (value) {
    if (value) {
      this.account = value
    }
  },

  /**
   * Sets handler to be called when session is being asked for
   * but we're not logged in yet.
   * Useful in non-interactive processes as a place to implement re-login.
   * @param {Function} value Login handler to call before obtaining the session
   */
  setLoginHandler (value) {
    if (value) {
      this.loginHandler = value
    }
  },

  /**
   * Sets error handler to be called when API calls fail with authorization errors
   * @param {Function<Error>} value Session error handler to call
   */
  setErrorHandler (value) {
    if (value) {
      this.errorHandler = value
    }
  },

  /**
   * Prepares authorization headers for a web request
   * using the specified token or the current session token
   * @param {String} token Session token
   * @returns {Dictionary<String, String>} Authorization headers
   */
  async getAuthorizationHeaders (token) {
    const createHeaders = token => ({ 'Authorization': `${token.startsWith('Bearer ') ? '' : 'Bearer '}${token}` })
    token = token || this.token

    if (token) {
      return createHeaders(token)
    } else {
      if (this.loginHandler) {
        await this.loginHandler()
        if (this.token) {
          return createHeaders(this.token)
        }
      }
    }

    return {}
  },

  /**
   * Clears the current session account
   */
  clear () {
    this.account = undefined
    this.token = undefined
    this.errorHandler = undefined
  }
}

/**
 * Impersonates the APIs with the specified account session.
 * @param {Session} session Session details
 * @param {Function<Error>} authErrorHandler Error handler to be called when API calls
 * fail with authorization errors. Can be used to capture expired
 * sessions and redirect accounts back to login page
 * @description
 * The credentials are required to perform secure calls to APIs.
 * Without them only a limited subset of APIs is available,
 * such as API status and login.
 */
export async function startAPISession (session = {}, authErrorHandler) {
  if (!session.token) throw new Error('Session token is required')

  APISession.setToken(session.token)
  if (session.account) {
    APISession.setAccount(session.account)
  }
  APISession.setErrorHandler(authErrorHandler)

  return APISession
}

/**
 * Clears the API session
 */
export async function clearAPISession () {
  APISession.clear()
  return APISession
}
